import React, { useEffect, useState } from "react"
import styles from "./SubscriptionResultPage.module.scss"
import PageLinedTitle from "../../components/elements/PageLinedTitle"
import ContentWithSidebars from "../../components/templates/ContentWithSidebars"
import { useLocation } from "react-router-dom"
import { trackSubscriptionStatus } from "../../services/subscriptionServices"
import axios from "../../services/axios"

// Helper function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const SubscriptionResultPage: React.FC = () => {
  const query = useQuery()
  const [status, setStatus] = useState<"success" | "error" | "loading">("loading")
  const [errorMessage, setErrorMessage] = useState<string>("")

  useEffect(() => {
    // Check for explicit status param first (new flow), then fall back to msg (legacy flow)
    const statusParam = query.get("status")
    const msg = query.get("msg")
    const planId = query.get("plan_id")
    const paymentFrequency = query.get("payment_frequency") || "annual"
    const seats = query.get("seats")
    const code = query.get("code") || undefined
    const sessionId = query.get("session_id") || undefined
    const reason = query.get("reason")

    // Get email from localStorage or sessionStorage if available
    const email = localStorage.getItem("userEmail") || sessionStorage.getItem("userEmail")

    // Success (new status param or legacy msg param)
    if (statusParam === "success" || msg === "checkout.success") {
      setStatus("success")

      // Track subscription completion
      if (email && planId) {
        // Track the status in our system
        trackSubscriptionStatus("subscription_completed", email, planId, undefined, code)

        // Call the backend to send confirmation email
        axios.post("/integrations/subscription/completed", {
          email,
          plan_id: planId,
          payment_frequency: paymentFrequency,
          seats: seats,
          code: code,
          session_id: sessionId
        }).catch(error => {
          console.error("Error notifying backend of subscription completion:", error)
        })
      }
    } else if (statusParam === "failure") {
      setStatus("error")
      setErrorMessage(reason || msg || "Subscription failed")
    } else if (msg) {
      // Legacy: any other msg is treated as an error
      setStatus("error")
      setErrorMessage(msg)
    } else {
      // If there's no status or msg, it's also an error
      setStatus("error")
      setErrorMessage("No status information provided")
    }
  }, [query])

  return (
    <div className={styles.pageWrap}>
      <PageLinedTitle title="Subscription Status" />

      <ContentWithSidebars>
        <div className={styles.resultContainer}>
          {status === "loading" && (
            <div className={styles.loadingContainer}>
              <p>Processing your subscription...</p>
            </div>
          )}

          {status === "success" && (
            <div className={styles.successContainer}>
              <div className={styles.iconContainer}>
                <span className={styles.successIcon}>✓</span>
              </div>
              <h2>Subscription Successful!</h2>
              <p>
                Thank you for subscribing to B2T. Your subscription has been processed successfully.
              </p>
              <p>
                You will receive a confirmation email shortly with details about your subscription.
              </p>
              <div className={styles.buttonContainer}>
                <a href="/" className={styles.button}>
                  Return to Home
                </a>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className={styles.errorContainer}>
              <div className={styles.iconContainer}>
                <span className={styles.errorIcon}>✗</span>
              </div>
              <h2>Subscription Failed</h2>
              <p>
                We encountered an issue while processing your subscription.
              </p>
              {errorMessage && (
                <div className={styles.errorDetails}>
                  <h3>Error Details:</h3>
                  <p>{errorMessage}</p>
                </div>
              )}
              <div className={styles.buttonContainer}>
                <a href="/paywall-sign-up" className={styles.button}>
                  Try Again
                </a>
                <a href="/" className={styles.buttonSecondary}>
                  Return to Home
                </a>
              </div>
            </div>
          )}
        </div>
      </ContentWithSidebars>
    </div>
  )
}

export default SubscriptionResultPage
